import React, { useState, useEffect } from 'react';
import { useAgentAuth } from '../contexts/AgentAuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Wallet, 
  TrendingUp, 
  CreditCard, 
  Calendar, 
  Users, 
  Copy, 
  CheckCircle,
  LogOut,
  BookOpen,
  Plus,
  Clock,
  MessageCircle,
  Tag,
  TrendingDown
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AgentDashboard = () => {
  const { agent, logout } = useAgentAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [subAgents, setSubAgents] = useState([]);
  const [announcements, setAnnouncements] = useState({ offers: [], news: [] });
  const [loading, setLoading] = useState(true);
  const [copiedReferral, setCopiedReferral] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('agentToken');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch stats
      const statsRes = await axios.get(`${API}/api/agent/dashboard/stats`, { headers });
      setStats(statsRes.data.stats);

      // Fetch recent bookings
      const bookingsRes = await axios.get(`${API}/api/agent/dashboard/bookings?limit=10`, { headers });
      setBookings(bookingsRes.data.bookings);

      // Fetch sub-agents/team
      const teamRes = await axios.get(`${API}/api/agent/dashboard/team`, { headers });
      setSubAgents(teamRes.data.subAgents);

      // Fetch announcements
      try {
        const announcementsRes = await axios.get(`${API}/api/agent/announcements`, { headers });
        setAnnouncements({
          offers: announcementsRes.data.offers || [],
          news: announcementsRes.data.news || []
        });
      } catch (error) {
        console.error('Failed to fetch announcements:', error);
      }

      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/agent/login');
  };

  const copyReferralCode = () => {
    if (agent?.referralCode) {
      navigator.clipboard.writeText(agent.referralCode);
      setCopiedReferral(true);
      setTimeout(() => setCopiedReferral(false), 2000);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'confirmed': return 'text-green-400 bg-green-500/10 border-green-500/20';
      case 'pending': return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
      case 'cancelled': return 'text-red-400 bg-red-500/10 border-red-500/20';
      default: return 'text-gray-400 bg-gray-500/10 border-gray-500/20';
    }
  };

  const getTierColor = (tier) => {
    switch(tier) {
      case 'tier1': return 'text-yellow-400 bg-yellow-500/10';
      case 'tier2': return 'text-blue-400 bg-blue-500/10';
      case 'tier3': return 'text-gray-400 bg-gray-500/10';
      default: return 'text-gray-400 bg-gray-500/10';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400"></div>
          <p className="text-white mt-4">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header */}
      <div className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <img src="/logo.png" alt="Indya Bungy" className="h-12 w-auto" />
              <div>
                <h1 className="text-2xl font-bold text-white">Agent Portal</h1>
                <p className="text-gray-400 text-sm">{agent?.agencyName}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right hidden sm:block">
                <p className="text-white font-medium">{agent?.contactPerson}</p>
                <p className="text-gray-400 text-sm">{agent?.agencyCode}</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
              >
                <LogOut size={18} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className={`grid grid-cols-1 md:grid-cols-2 ${agent?.tier === 'tier4' ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-6 mb-8`}>
          {/* Wallet Balance */}
          <div className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/10 border border-yellow-500/20 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <Wallet className="text-yellow-400" size={32} />
              <span className={`px-3 py-1 ${getTierColor(stats?.tier)} rounded-full text-xs font-bold uppercase`}>
                {stats?.tier}
              </span>
            </div>
            <p className="text-gray-400 text-sm mb-1">Wallet Balance</p>
            <p className="text-3xl font-bold text-white">₹{stats?.walletBalance?.toLocaleString() || 0}</p>
          </div>

          {/* Total Earnings */}
          <div className="bg-gradient-to-br from-green-500/10 to-green-600/10 border border-green-500/20 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="text-green-400" size={32} />
            </div>
            <p className="text-gray-400 text-sm mb-1">Total Earnings</p>
            <p className="text-3xl font-bold text-white">₹{stats?.totalEarnings?.toLocaleString() || 0}</p>
          </div>

          {/* Credit Status - Hide for Tier 4 */}
          {agent?.tier !== 'tier4' && (
            <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border border-blue-500/20 rounded-xl p-6">
              <div className="flex items-center justify-between mb-2">
                <CreditCard className="text-blue-400" size={32} />
              </div>
              <p className="text-gray-400 text-sm mb-1">Available Credit</p>
              <p className="text-3xl font-bold text-white">₹{stats?.availableCredit?.toLocaleString() || 0}</p>
              <p className="text-gray-500 text-xs mt-1">Limit: ₹{stats?.creditLimit?.toLocaleString() || 0}</p>
            </div>
          )}

          {/* Total Bookings */}
          <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 border border-purple-500/20 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="text-purple-400" size={32} />
            </div>
            <p className="text-gray-400 text-sm mb-1">Total Bookings</p>
            <p className="text-3xl font-bold text-white">{stats?.totalBookings || 0}</p>
            <p className="text-gray-500 text-xs mt-1">Today: {stats?.todayBookings || 0}</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Referral Code */}
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
            <h3 className="text-white font-bold mb-4 flex items-center">
              <Users className="mr-2 text-yellow-400" size={20} />
              Your Referral Code
            </h3>
            <div className="flex items-center space-x-3">
              <div className="flex-1 bg-gray-900 border border-gray-600 rounded-lg px-4 py-3">
                <p className="text-yellow-400 font-mono text-lg font-bold">{agent?.referralCode || 'N/A'}</p>
              </div>
              <button
                onClick={copyReferralCode}
                className="p-3 bg-yellow-400 hover:bg-yellow-500 rounded-lg transition-colors"
              >
                {copiedReferral ? <CheckCircle className="text-black" size={20} /> : <Copy className="text-black" size={20} />}
              </button>
            </div>
            <p className="text-gray-400 text-xs mt-3">Share this code with new agents to earn from their bookings</p>
            <p className="text-gray-500 text-xs mt-1">Sub-agents: {stats?.subAgentsCount || 0}</p>
          </div>

          {/* Quick Actions */}
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
            <h3 className="text-white font-bold mb-4 flex items-center">
              <BookOpen className="mr-2 text-yellow-400" size={20} />
              Quick Actions
            </h3>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/agent/booking/create')}
                className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-3 rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                <Plus size={20} />
                <span>Create New Booking</span>
              </button>
              <button
                onClick={() => navigate('/agent/slots')}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                <Clock size={20} />
                <span>View Available Slots</span>
              </button>
              <button
                onClick={() => navigate('/agent/send-message')}
                className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                <MessageCircle size={20} />
                <span>Send Info to Customer</span>
              </button>
            </div>
          </div>

          {/* Credit Info - Hide for Tier 4 */}
          {agent?.tier !== 'tier4' && (
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
              <h3 className="text-white font-bold mb-4">Credit Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Credit Limit</span>
                  <span className="text-white font-bold">₹{stats?.creditLimit?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Used</span>
                  <span className="text-red-400 font-bold">₹{stats?.creditUsed?.toLocaleString() || 0}</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-yellow-400 transition-all"
                    style={{ width: `${stats?.creditLimit ? (stats.creditUsed / stats.creditLimit * 100) : 0}%` }}
                  />
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Available</span>
                  <span className="text-green-400 font-bold">₹{stats?.availableCredit?.toLocaleString() || 0}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Recent Bookings */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-white font-bold text-xl">Recent Bookings</h3>
            <button
              onClick={() => navigate('/agent/bookings')}
              className="text-yellow-400 hover:text-yellow-300 text-sm font-medium"
            >
              View All →
            </button>
          </div>
          
          {bookings.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="mx-auto mb-4 text-gray-600" size={48} />
              <p className="text-gray-400">No bookings yet</p>
              <p className="text-gray-500 text-sm mt-2">Create your first booking to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left text-gray-400 font-medium py-3 px-4">Booking ID</th>
                    <th className="text-left text-gray-400 font-medium py-3 px-4">Customer</th>
                    <th className="text-left text-gray-400 font-medium py-3 px-4">Date</th>
                    <th className="text-left text-gray-400 font-medium py-3 px-4">Jumpers</th>
                    <th className="text-left text-gray-400 font-medium py-3 px-4">Amount</th>
                    <th className="text-left text-gray-400 font-medium py-3 px-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => (
                    <tr key={booking._id} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                      <td className="py-3 px-4 text-white font-mono text-sm">{booking.bookingId}</td>
                      <td className="py-3 px-4 text-white">{booking.customerName}</td>
                      <td className="py-3 px-4 text-gray-400">{new Date(booking.bookingDate).toLocaleDateString()}</td>
                      <td className="py-3 px-4 text-white">{booking.numberOfJumpers}</td>
                      <td className="py-3 px-4 text-white font-bold">₹{booking.totalAmount?.toLocaleString()}</td>
                      <td className="py-3 px-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(booking.status)}`}>
                          {booking.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Offers & News Sections */}
        {(announcements.offers.length > 0 || announcements.news.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Offers Section */}
            {announcements.offers.length > 0 && (
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
                <h3 className="text-white font-bold text-xl mb-6 flex items-center">
                  <Tag className="mr-2 text-green-400" size={24} />
                  Current Offers
                </h3>
                <div className="space-y-4">
                  {announcements.offers.slice(0, 3).map((offer) => (
                    <div key={offer._id} className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="text-white font-bold">{offer.title}</h4>
                        {offer.priority === 'high' && (
                          <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs font-bold">HOT</span>
                        )}
                      </div>
                      <p className="text-gray-300 text-sm">{offer.content}</p>
                      {offer.validUntil && (
                        <p className="text-gray-500 text-xs mt-2">Valid until: {new Date(offer.validUntil).toLocaleDateString()}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* News Section */}
            {announcements.news.length > 0 && (
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
                <h3 className="text-white font-bold text-xl mb-6 flex items-center">
                  <TrendingDown className="mr-2 text-blue-400" size={24} />
                  Latest News
                </h3>
                <div className="space-y-4">
                  {announcements.news.slice(0, 3).map((news) => (
                    <div key={news._id} className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="text-white font-bold">{news.title}</h4>
                        {news.priority === 'high' && (
                          <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs font-bold">NEW</span>
                        )}
                      </div>
                      <p className="text-gray-300 text-sm">{news.content}</p>
                      <p className="text-gray-500 text-xs mt-2">{new Date(news.createdAt).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sub-Agents Section */}
        {subAgents.length > 0 && (
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
            <h3 className="text-white font-bold text-xl mb-6 flex items-center">
              <Users className="mr-2 text-yellow-400" size={24} />
              Your Team ({subAgents.length} Sub-Agents)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {subAgents.map((subAgent) => (
                <div key={subAgent._id} className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <p className="text-white font-bold">{subAgent.agencyName}</p>
                    <span className={`px-2 py-1 rounded text-xs ${subAgent.approvalStatus === 'approved' ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}`}>
                      {subAgent.approvalStatus}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm">{subAgent.contactPerson}</p>
                  <p className="text-gray-500 text-xs mt-1">{subAgent.agencyCode}</p>
                  <p className="text-gray-400 text-xs mt-2">Bookings: {subAgent.totalBookings || 0}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentDashboard;